import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { randomId } from 'server/shared/helpers/random';
import { InviteeFile } from 'server/shared/models/inviteeFile';
import {
  UploadFile,
  UserCustomerInformation,
} from 'server/shared/models/userCustomerInformation';
import { downloadFile } from 'src/helpers/downloadFile';
import { ToastService } from '../../../services/toast/toast.service';
import { UserWithCustomer } from '../profile-edit.component';

@Component({
  selector: 'app-cv-upload',
  templateUrl: './cv-upload.component.html',
  styleUrls: ['./cv-upload.component.scss'],
})
export class CvUploadComponent implements OnInit {
  @Input() customerInformation?: UserCustomerInformation;
  @Input() loggedInUserWithCustomer?: UserWithCustomer;
  @Input() isUploading: boolean = false;
  @Input() isNewUser: boolean = false;
  @Output() temporaryFilesChange = new EventEmitter<InviteeFile[]>();
  poolerId?: string;
  isDownloading?: boolean = false;
  progressbarValue: number = 0;
  temporaryFiles: InviteeFile[] = [];

  constructor(
    private route: ActivatedRoute,
    private toastService: ToastService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.poolerId = this.route.snapshot.queryParams['userId'];
  }

  uploadFile(event: Event) {
    if (!this.loggedInUserWithCustomer?.customer.id) {
      return;
    }

    if (this.isNewUser && !this.poolerId) {
      this.poolerId = 'temp-' + randomId();
    }

    if (!this.poolerId) {
      return;
    }

    this.isUploading = true;
    const files = (event.target as HTMLInputElement).files;
    if (!FileReader || !files || !files.length) {
      this.toastService.add({ type: 'ERROR', message: 'notSupported' });
      return;
    } else {
      Array.from(files).forEach((uploadFile) => {
        const file = uploadFile as File;
        if (file.size < 1024 * 1024 * 15) {
          const fileReader = new FileReader();
          fileReader.onload = () => {
            if (this.isNewUser) {
              const fileId = randomId();
              const inviteeFile: InviteeFile = {
                _id: fileId,
                customerId: this.loggedInUserWithCustomer!.customer.id,
                fileName: file.name,
                createdAt: Date.now(),
                updatedAt: Date.now(),
                path: fileId,
                size: file.size.toString(),
                type: file.type,
                blobString: fileReader.result?.toString(),
              };

              Meteor.call(
                'upsertInviteeFile',
                inviteeFile,
                (error: Meteor.Error, result: InviteeFile) => {
                  if (error) {
                    this.isUploading = false;
                    this.toastService.add({
                      type: 'ERROR',
                      message: 'serverError',
                    });
                    return;
                  }
                  if (result) {
                    this.isUploading = false;
                    this.temporaryFiles.push(result);
                    this.temporaryFilesChange.emit(this.temporaryFiles);
                    this.toastService.add({
                      type: 'SUCCESS',
                      message: 'documentUploaded',
                    });
                  }
                },
              );
            } else {
              const fileUpload: UploadFile = {
                id: randomId(),
                fileName: file.name,
                size: file.size.toString(),
                path: this.poolerId || '',
                timestamp: Date.now(),
                type: file.type,
                secret: '',
              };
              Meteor.apply(
                'updateDocFile',
                [
                  {
                    userId: this.poolerId,
                    customerId: this.loggedInUserWithCustomer?.customer.id,
                    fileUpload: fileUpload,
                    blobData: fileReader.result?.toString(),
                  },
                ],
                {
                  wait: true,
                  onResultReceived: (error, result: any) => {
                    if (error) {
                      this.isUploading = false;
                      this.toastService.add({
                        type: 'ERROR',
                        message: 'serverError',
                      });
                    }
                    if (result) {
                      this.isUploading = false;
                      this.toastService.add({
                        type: 'SUCCESS',
                        message: 'documentUploaded',
                      });
                    }
                  },
                },
              );
            }
          };
          fileReader.readAsDataURL(file);
        } else {
          this.isUploading = false;
          this.toastService.add({
            type: 'ERROR',
            message: 'maxFileUploadExceeded',
          });
        }
      });
    }
  }

  removeFileDialog(file: UploadFile | InviteeFile, templateRef: any) {
    const dialogRef = this.dialog.open(templateRef);
    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        this.removeFile(file);
      }
    });
  }

  removeFile(file: UploadFile | InviteeFile) {
    if (this.isNewUser) {
      const inviteeFile = file as InviteeFile;
      Meteor.call(
        'removeInviteeFile',
        inviteeFile,
        (error: Meteor.Error, result: InviteeFile) => {
          if (error) {
            this.toastService.add({ type: 'ERROR', message: 'serverError' });
            return;
          }
          if (result) {
            this.temporaryFiles = this.temporaryFiles.filter(
              (f) => f._id !== result._id,
            );
            this.temporaryFilesChange.emit(this.temporaryFiles);
            this.toastService.add({
              type: 'SUCCESS',
              message: 'removedSuccessfully',
            });
          }
        },
      );
    } else {
      Meteor.apply(
        'removeFile',
        [
          {
            userId: this.poolerId,
            customerId: this.loggedInUserWithCustomer?.customer.id,
            file: file as UploadFile,
          },
        ],
        {
          wait: true,
          onResultReceived: (error, result: any) => {
            if (error) {
              this.toastService.add({ type: 'ERROR', message: 'serverError' });
              return;
            }
            if (result) {
              this.toastService.add({
                type: 'SUCCESS',
                message: 'removedSuccessfully',
              });
            }
          },
        },
      );
    }
  }

  downloadFile(event: Event, file: UploadFile | InviteeFile) {
    this.isDownloading = true;
    if (this.isNewUser) {
      const inviteeFile = file as InviteeFile;
      downloadFile(inviteeFile, '', (percentage: number) => {
        this.progressbarValue = percentage;
      })
        .then(() => {
          this.isDownloading = false;
        })
        .catch(() => {
          this.toastService.add({
            type: 'ERROR',
            message: 'fileNotFound',
          });
          this.isDownloading = false;
        });
    } else {
      if (!this.poolerId) {
        return;
      }
      downloadFile(file as UploadFile, this.poolerId, (percentage: number) => {
        this.progressbarValue = percentage;
      })
        .then(() => {
          this.isDownloading = false;
        })
        .catch(() => {
          this.toastService.add({
            type: 'ERROR',
            message: 'fileNotFound',
          });
          this.isDownloading = false;
        });
    }
  }
}
